import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  AlertCircle, User, Calendar, Droplets, AlertTriangle, 
  Pill, Heart, Stethoscope, Phone, Shield, ShieldAlert
} from 'lucide-react';

/**
 * EmergencyViewPage - Public read-only emergency summary
 * Accessible via QR code without authentication
 * Shows critical medical information for EMTs and first responders
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmergencyViewPage = () => {
  const { accessToken } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    fetchPublicSummary();
  }, [accessToken]);

  const fetchPublicSummary = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/emergency-binder/public/${accessToken}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          setError('Emergency information not found or link has expired.');
        } else if (response.status === 403) {
          setError('This emergency information is no longer shared.');
        } else {
          setError('Unable to load emergency information.');
        }
        return;
      }
      
      const data = await response.json();
      setSummary(data);
    } catch (err) {
      console.error('Failed to fetch public summary:', err);
      setError('Unable to load emergency information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-red-600 flex flex-col items-center justify-center p-4">
        <ShieldAlert className="h-16 w-16 text-white animate-pulse mb-4" />
        <p className="text-white text-xl font-semibold">Loading Emergency Info...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center p-6" data-testid="emergency-view-error">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-lg">
          <AlertCircle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Access Unavailable</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <Button
            onClick={() => navigate('/login')}
            className="w-full"
          >
            Go to App
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-red-600 text-white" data-testid="emergency-view-page">
      {/* Header */}
      <header className="p-4 text-center border-b border-red-500">
        <div className="flex items-center justify-center gap-2 mb-2">
          <ShieldAlert className="h-8 w-8" />
          <span className="font-bold text-xl">EMERGENCY MEDICAL INFO</span>
        </div>
        <p className="text-red-200 text-sm">Critical information for first responders</p>
      </header>

      <main className="px-4 py-6 space-y-4 max-w-lg mx-auto">
        {/* Name & Basic Info */}
        <div className="text-center py-4 border-b border-red-500">
          <div className="flex items-center justify-center gap-3 mb-2">
            <User className="h-8 w-8" />
            <h1 className="text-3xl font-bold">{summary?.profile_name}</h1>
          </div>
          {summary?.date_of_birth && (
            <p className="text-red-200 text-lg flex items-center justify-center gap-2">
              <Calendar className="h-5 w-5" />
              DOB: {summary.date_of_birth}
            </p>
          )}
        </div>

        {/* Blood Type - Most Critical */}
        {summary?.blood_type && (
          <div className="bg-white text-red-600 rounded-2xl p-6 text-center">
            <div className="flex items-center justify-center gap-3 mb-2">
              <Droplets className="h-8 w-8" />
              <span className="text-xl font-semibold">Blood Type</span>
            </div>
            <p className="text-6xl font-black">{summary.blood_type}</p>
          </div>
        )}

        {/* Allergies - Critical Warning */}
        <div className="bg-amber-500 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="h-8 w-8" />
            <span className="text-2xl font-bold">ALLERGIES</span>
          </div>
          {summary?.allergies?.length > 0 ? (
            <ul className="space-y-3">
              {summary.allergies.map((allergy, index) => (
                <li key={index} className="text-2xl font-semibold flex items-center gap-3">
                  <span className="w-3 h-3 bg-white rounded-full flex-shrink-0" />
                  {allergy}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-2xl font-semibold">No Known Allergies</p>
          )}
        </div>

        {/* Medications */}
        <div className="bg-blue-500 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Pill className="h-8 w-8" />
            <span className="text-2xl font-bold">MEDICATIONS</span>
          </div>
          {summary?.medications?.length > 0 ? (
            <ul className="space-y-3">
              {summary.medications.map((med, index) => (
                <li key={index} className="text-xl font-semibold flex items-center gap-3">
                  <span className="w-3 h-3 bg-white rounded-full flex-shrink-0" />
                  {med}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xl font-semibold">No Current Medications</p>
          )}
        </div>

        {/* Medical Conditions */}
        {summary?.conditions?.length > 0 && (
          <div className="bg-purple-600 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Heart className="h-8 w-8" />
              <span className="text-2xl font-bold">CONDITIONS</span>
            </div>
            <ul className="space-y-3">
              {summary.conditions.map((condition, index) => (
                <li key={index} className="text-xl font-semibold flex items-center gap-3">
                  <span className="w-3 h-3 bg-white rounded-full flex-shrink-0" />
                  {condition}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Emergency Contact */}
        {summary?.emergency_contact && (
          <div className="bg-emerald-600 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Phone className="h-8 w-8" />
              <span className="text-2xl font-bold">EMERGENCY CONTACT</span>
            </div>
            <p className="text-2xl font-bold mb-1">{summary.emergency_contact}</p>
            {summary.emergency_contact_relationship && (
              <p className="text-lg text-emerald-200 mb-3">({summary.emergency_contact_relationship})</p>
            )}
            {summary.emergency_contact_phone && (
              <a 
                href={`tel:${summary.emergency_contact_phone}`}
                className="inline-block bg-white text-emerald-600 px-8 py-4 rounded-xl text-2xl font-bold"
              >
                CALL: {summary.emergency_contact_phone}
              </a>
            )}
          </div>
        )}

        {/* Primary Doctor */}
        {summary?.primary_doctor && (
          <div className="bg-slate-700 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Stethoscope className="h-8 w-8" />
              <span className="text-2xl font-bold">PRIMARY DOCTOR</span>
            </div>
            <p className="text-2xl font-bold mb-1">{summary.primary_doctor}</p>
            {summary.primary_doctor_phone && (
              <a 
                href={`tel:${summary.primary_doctor_phone}`}
                className="inline-block bg-white text-slate-700 px-6 py-3 rounded-xl text-xl font-bold mt-2"
              >
                {summary.primary_doctor_phone}
              </a>
            )}
          </div>
        )}

        {/* Insurance */}
        {summary?.insurance_provider && (
          <div className="bg-pink-600 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="h-8 w-8" />
              <span className="text-2xl font-bold">INSURANCE</span>
            </div>
            <p className="text-xl font-bold mb-1">{summary.insurance_provider}</p>
            {summary.insurance_policy_number && (
              <p className="text-lg text-pink-200">Policy #: {summary.insurance_policy_number}</p>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="text-center py-6 border-t border-red-500 mt-6">
          <p className="text-red-200 text-sm">
            This information was shared by the patient for emergency use.
          </p>
          <p className="text-red-300 text-xs mt-2">
            Powered by Forget Me Not Emergency Binder
          </p>
        </div>
      </main>
    </div>
  );
};

export default EmergencyViewPage;
